/**
 * GOAT-OVERRIDE(pdp-section-render): narrow the PDP variant-change request to one section.
 *
 * `assets/variant-picker.js` is byte-identical to pristine Horizon 4.1.5, so this patches its
 * prototype from a GOAT-owned module instead of editing it — nothing to re-apply after the next
 * vendor merge beyond confirming upstream's own `SECTION_ID_MAP` still has no PDP entry.
 *
 * Upstream maps only three hosts (`quick-add-component`, `swatches-variant-picker-component`,
 * `featured-product-information`) to a `section_id`. On a real product page none of them match, so
 * `buildRequestUrl` falls through to `${productUrl}?option_values=…` — a request for the ENTIRE
 * product page. Shopify then re-renders every section in templates/product.json (the product grid,
 * the curated review row, the lifestyle collection, product recommendations with their related-
 * products lookup, the FAQ, header and footer) and ships them all, while the client keeps exactly
 * one: the main product section. Every product card in those rows re-runs the swatch resolver, so
 * the shopper waits on work nothing displays.
 *
 * Every consumer of the response reads a node that lives inside the main product section —
 * media-gallery, product-price, product-sku, product-inventory, product-form-component,
 * quantity-selector-component, price-per-item, sticky-add-to-cart and loyalty-points — and the
 * variant JSON the picker itself parses is rendered by snippets/variant-main-picker.liquid, also
 * inside it. The section id is read from the DOM rather than hardcoded, so alternate product
 * templates keep working.
 *
 * Deliberately NOT applied when:
 *   - upstream already chose a section (the three mapped hosts);
 *   - the click loads a different product (combined listings). `updateMain()` does
 *     `newHtml.querySelector('main')` and throws without it, and a `?section_id=` response has no
 *     <main>;
 *   - the picker has no `.shopify-section` ancestor, which is the case inside the layout-level
 *     Quick Shop / quick-add dialogs.
 */
import VariantPicker from '@theme/variant-picker';

const SECTION_ID_PREFIX = 'shopify-section-';
const originalBuildRequestUrl = VariantPicker.prototype.buildRequestUrl;

VariantPicker.prototype.buildRequestUrl = function goatBuildRequestUrl(selectedOption, ...rest) {
  const url = originalBuildRequestUrl.call(this, selectedOption, ...rest);

  try {
    if (typeof url !== 'string' || url.includes('section_id=')) return url;

    // Combined listing: the response must be a full document so `updateMain()` finds <main>.
    if (selectedOption?.dataset?.connectedProductUrl) return url;

    const sectionElement = this.closest('.shopify-section');
    const sectionId = sectionElement?.id;
    if (!sectionId || !sectionId.startsWith(SECTION_ID_PREFIX)) return url;

    const section = sectionId.slice(SECTION_ID_PREFIX.length);
    if (!section) return url;

    return `${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(section)}`;
  } catch {
    // Never let the narrowing break a variant change; fall back to upstream's URL.
    return url;
  }
};
